import { regionOfLocation, cityOfLocation } from '@/shared/regions'
import { isUnreachable } from '@/features/leads/workflow/queue'
import {
  EMPTY_DATE, EMPTY_NUM, EMPTY_TEXT, emptySet, isDateActive, isNumActive,
  isSetActive, isTextActive, matchesDate, matchesNum, matchesSet, matchesText,
  type DateFilter, type NumFilter, type SetFilter, type TextFilter,
} from './filterModel'
import type { ContactInfoFilter } from '@/features/leads/workflow/queue'
import type { FilterValue } from './ColumnFilter'
import type { Stage } from '@/shared/domain'
import type { EmailStatus, LeadRow } from '@/shared/types'
import { STAGE_TEXT_CLASS } from '@/shared/pipelineColors'
import { sortableScore, underlag } from '@/features/leads/score'


export const CALL_OUTCOMES = [
  'Reached',
  'Voicemail',
  'No answer',
  'Asked to call back',
  'Gatekeeper',
  'Not interested',
  'Wrong number',
  'Hung up',
] as const
export type CallOutcome = (typeof CALL_OUTCOMES)[number]

// Display only — the value logged to the timeline (CallDraft.outcome) stays
// the English key above, the same key/label split as Stage/STAGE_LABEL, so a
// backend or admin screen reading a logged outcome never has to know this
// file renders it in Swedish.
export const CALL_OUTCOME_LABEL: Record<CallOutcome, string> = {
  'Reached': 'Kontakt',
  'Voicemail': 'Röstbrevlåda',
  'No answer': 'Inget svar',
  'Asked to call back': 'Bad om återuppringning',
  'Gatekeeper': 'Grindvakt',
  'Not interested': 'Inte intresserad',
  'Wrong number': 'Fel nummer',
  'Hung up': 'La på',
}

export interface CallDraft {
  leadId: string
  index: number
  company: string
  outcome: CallOutcome | null
  note: string
}

// Sortable columns: anything where ordering is meaningful. Other columns can
// still be filtered but don't expose sort buttons.
export type SortKey =
  | 'score'
  | 'underlag'
  | 'company'
  | 'region'
  | 'city'
  | 'contact'
  | 'phone'
  | 'email'
  | 'callCount'
  | 'gotContact'
  | 'rating'
  | 'stage'
  | 'sfStatus'
  | 'booked'
  | 'likely'
  | 'leadDate'
  | 'moveDate'
  | 'meetingDate'
  | 'seller'
  | 'owner'
  | 'orgNo'

export interface ColumnSort {
  key: SortKey
  dir: 'asc' | 'desc'
}

// Filter state — Set<string> per categorical column (empty = no filter),
// string per text column (empty = no filter). Numeric columns are bucketed for
// raw for sort. `global` searches across company / contact / org no.
export interface Filters {
  /** Free-text across the identity fields; never column-scoped. */
  global: string

  // Text columns — each carries its own operator.
  company: TextFilter
  contact: TextFilter
  orgNo: TextFilter

  // Categorical columns — a value set plus include/exclude.
  region: SetFilter
  city: SetFilter
  scoreBucket: SetFilter
  hasPhone: SetFilter
  emailStatus: SetFilter
  callCount: SetFilter
  gotContact: SetFilter
  rating: SetFilter
  stage: SetFilter
  sfStatus: SetFilter
  booked: SetFilter
  likely: SetFilter
  leadDate: SetFilter
  moveDate: SetFilter
  meetingDate: SetFilter
  seller: SetFilter
  owner: SetFilter

  // Ranges, additive to the buckets above rather than replacing them: ticking
  // "90 – 100" is quicker than typing bounds and the buckets carry live counts,
  // so both apply together.
  scoreRange: NumFilter
  callCountRange: NumFilter
  ratingRange: NumFilter
  leadDateRange: DateFilter
  moveDateRange: DateFilter
  meetingDateRange: DateFilter

  // Three-way contact-info filter — shared definition with Focus mode.
  // 'all' (default) shows everything, 'reachable' hides leads with no email and
  // no phone, 'unreachable' shows ONLY those.
  contactInfo: ContactInfoFilter

  // Leads marked Kontakta ej: shown with the rest ('all', the default), shown
  // alone ('only'), or left out ('hide').
  doNotContact: DoNotContactFilter
}

export type DoNotContactFilter = 'all' | 'only' | 'hide'

export function isValidDoNotContactFilter(v: unknown): v is DoNotContactFilter {
  return v === 'all' || v === 'only' || v === 'hide'
}

export function matchesDoNotContact(l: LeadRow, f: DoNotContactFilter): boolean {
  if (f === 'all') return true
  return !!l.doNotContact === (f === 'only')
}

export const EMPTY_FILTERS: Filters = {
  global: '',
  company: EMPTY_TEXT,
  contact: EMPTY_TEXT,
  orgNo: EMPTY_TEXT,
  region: emptySet(),
  city: emptySet(),
  scoreBucket: emptySet(),
  hasPhone: emptySet(),
  emailStatus: emptySet(),
  callCount: emptySet(),
  gotContact: emptySet(),
  rating: emptySet(),
  stage: emptySet(),
  sfStatus: emptySet(),
  booked: emptySet(),
  likely: emptySet(),
  leadDate: emptySet(),
  moveDate: emptySet(),
  meetingDate: emptySet(),
  seller: emptySet(),
  owner: emptySet(),
  scoreRange: EMPTY_NUM,
  callCountRange: EMPTY_NUM,
  ratingRange: EMPTY_NUM,
  leadDateRange: EMPTY_DATE,
  moveDateRange: EMPTY_DATE,
  meetingDateRange: EMPTY_DATE,
  contactInfo: 'all',
  doNotContact: 'all',
}


export const SCORE_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: '90 – 100', min: 90, max: 100 },
  { label: '80 – 89',  min: 80, max: 89 },
  { label: '70 – 79',  min: 70, max: 79 },
  { label: '60 – 69',  min: 60, max: 69 },
  { label: '< 60',     min: 0,  max: 59 },
]
// No bucket a missing score could fall into without reading as a bad score
// rather than an absent one — a null stays its own bucket.
export const NO_SCORE_LABEL = 'Ingen poäng'

// Label for the Owner column and its filter dropdown. Local part only — the
// full address is in the cell's title. '(otilldelad)' is a real, selectable
// value: "show me what nobody has taken" is the most useful owner filter there
// is, so it must not be an empty string that the dropdown would hide.
export const UNCLAIMED_LABEL = '(otilldelad)'

export function ownerLabelOf(l: LeadRow): string {
  return l.assignedTo ? l.assignedTo.split('@')[0] : UNCLAIMED_LABEL
}

/** Label for a lead with no Salesforce lead behind it. A real, selectable
 *  value for the same reason UNCLAIMED_LABEL is: "which of these has nobody in
 *  Salesforce touched" is a question worth being able to ask. */
export const NOT_IN_SF_LABEL = '(inte i Salesforce)'

/**
 * The Salesforce status column's value for one lead.
 *
 * Whatever Salesforce said, unchanged. The real values are Swedish
 * (`Arbetar`, `Kvalificerad/Intressant`, …) with a run of stray English ones,
 * and the set is not closed — so there is deliberately no list and no
 * translation map here. Rendering the raw value is what keeps a status nobody
 * has seen before from vanishing out of the filter.
 */
export function sfStatusLabelOf(l: LeadRow): string {
  const s = (l.sfStatus || '').trim()
  return s || NOT_IN_SF_LABEL
}

export function scoreBucketOf(score: number | null): string {
  if (score == null) return NO_SCORE_LABEL
  for (const b of SCORE_BUCKETS) {
    if (score >= b.min && score <= b.max) return b.label
  }
  return '< 60'
}

export function leadDateBucketOf(iso: string): string {
  if (!iso) return 'Okänt'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return 'Okänt'
  const now = new Date()
  const ms = now.getTime() - d.getTime()
  const day = 24 * 60 * 60 * 1000
  if (ms < day) return 'Idag'
  if (ms < 2 * day) return 'Igår'
  if (ms < 7 * day) return 'Denna vecka'
  if (ms < 30 * day) return 'Denna månad'
  if (ms < 90 * day) return 'Senaste 3 månaderna'
  return 'Äldre'
}
export const LEAD_DATE_ORDER = ['Idag', 'Igår', 'Denna vecka', 'Denna månad', 'Senaste 3 månaderna', 'Äldre', 'Okänt']

// Planned-move date is forward-looking — bucket by how far out the move is so
// the BDR can pull "moving soon" lists. A date already in the past is its own
// bucket (the move happened / slipped); no date at all sorts last.
export function moveDateBucketOf(iso: string | null | undefined): string {
  if (!iso) return 'Inget datum'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return 'Inget datum'
  const day = 24 * 60 * 60 * 1000
  const ms = d.getTime() - new Date().getTime()
  if (ms < 0) return 'Passerat'
  if (ms < 30 * day) return 'Denna månad'
  if (ms < 90 * day) return '1–3 månader'
  if (ms < 180 * day) return '3–6 månader'
  if (ms < 365 * day) return '6–12 månader'
  return '12+ månader'
}
export const MOVE_DATE_ORDER = ['Passerat', 'Denna månad', '1–3 månader', '3–6 månader', '6–12 månader', '12+ månader', 'Inget datum']

// Booked-meeting date, bucketed for the manual follow-up worklist: a meeting
// already in the past is the actionable bucket (it happened — follow up), then
// upcoming windows. No booked meeting sorts last.
export function meetingDateBucketOf(iso: string | null | undefined): string {
  if (!iso) return 'Inget datum'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return 'Inget datum'
  const day = 24 * 60 * 60 * 1000
  const ms = d.getTime() - new Date().getTime()
  if (ms < 0) return 'Passerat'
  if (ms < day) return 'Idag'
  if (ms < 7 * day) return 'Denna vecka'
  if (ms < 30 * day) return 'Denna månad'
  return 'Senare'
}
export const MEETING_DATE_ORDER = ['Passerat', 'Idag', 'Denna vecka', 'Denna månad', 'Senare', 'Inget datum']

export const EMAIL_STATUS_LABEL: Record<EmailStatus, string> = {
  deliverable: 'Levererbar',
  risky: 'Riskabel',
  undeliverable: 'Studsat',
  unknown: 'Okänd',
}

export const EMAIL_STATUS_CLASS: Record<EmailStatus, string> = {
  deliverable: 'text-ink-700',
  risky: 'text-ink-700',
  undeliverable: 'text-ink-300 line-through',
  // `unknown` is the fallback for anything we have not verified, which is most
  // leads — so this was the *common* case rendering at 1.85:1, and the address a
  // BDR needs to read was the least readable thing in the row. The italic still
  // marks it as unverified; ink-300 was doing that job twice and costing legibility.
  unknown: 'text-ink-500 italic',
}

// The phone column is pinned at 126px. A lead with two numbers ran on past it
// into the e-mail column, so the number stays in the cell and ends in an
// ellipsis, with the whole value in the cell's title. Same overflow as e-mail.
export const PHONE_CELL_CLASS = 'overflow-hidden text-ellipsis whitespace-nowrap'

// Same map as StageBar/the admin activity legend — see shared/pipelineColors.
// 'Disqualified' keeps its italic (closed-lost is the one stage that also
// reads as de-emphasized), the other 7 take the shared colour as-is.
export const STAGE_SELECT_CLASS: Record<Stage, string> = {
  ...STAGE_TEXT_CLASS,
  'Disqualified': `${STAGE_TEXT_CLASS['Disqualified']} italic`,
}

// Build a sorted unique-value list with counts. Used by every categorical filter.
export function buildValues(
  leads: LeadRow[],
  key: (l: LeadRow) => string,
  order?: string[],
  /**
   * Rendered text for a value. `value` itself is what the filter matches on and
   * what gets persisted, so it must stay the real string — this only changes
   * what the row says. That separation is why white-label can mask the owner
   * and seller dropdowns without breaking filtering.
   */
  label?: (value: string) => string,
): FilterValue[] {
  const counts = new Map<string, number>()
  for (const l of leads) {
    const v = key(l) || '(tomt)'
    counts.set(v, (counts.get(v) || 0) + 1)
  }
  const entries = Array.from(counts.entries())
  if (order) {
    entries.sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
  } else {
    entries.sort(([a], [b]) => a.localeCompare(b))
  }
  return entries.map(([value, count]) => ({ value, label: label ? label(value) : value, count }))
}

export const LS_KEY = 'table.viewState'
/** Read once so a rename does not discard saved views. See lib/storage. */
export const LS_KEY_LEGACY = 'crm-table-view-state'
// Bump whenever the persisted shape changes; stored state at another version
// is discarded rather than half-read.
export const STATE_VERSION = 5
// Newest lead first. This was score-descending until CRM-84 removed the score;
// the table is a browsing view, so recency is the useful default there — it is
// not a priority order, and the queue deliberately has none. Bringing the
// column back (YCRM-12) does not change that: the score still only informs.
export const DEFAULT_SORT: ColumnSort = { key: 'leadDate', dir: 'desc' }

// One-click named worklists surfaced above the toolbar search. A view is a
// shortcut across the two axes it owns — the Stage subset and the contact-info
// three-way — and clicking one replaces both, so exactly one view is ever
// active and no filter survives from the view before it. Every other column
// filter is untouched, which is what still allows "no contact, in Skåne".
export interface QuickView {
  id: string
  label: string
  stages: string[]  // empty = no Stage filter (the "All" view)
  contactInfo: ContactInfoFilter
  /** Tooltip, for a view whose label alone does not say what is in it. */
  hint?: string
}

export const QUICK_VIEWS: QuickView[] = [
  { id: 'all',           label: 'Alla',          stages: [],                     contactInfo: 'all' },
  { id: 'fresh',         label: 'Nya',           stages: ['New'],                contactInfo: 'all' },
  { id: 'in-progress',   label: 'Pågående',      stages: ['Calling', 'Contacted'], contactInfo: 'all' },
  { id: 'booked',        label: 'Bokade',        stages: ['Booked'],             contactInfo: 'all' },
  { id: 'wrong-contact', label: 'Fel kontakt',   stages: ['Wrong Contact'],      contactInfo: 'all' },
  { id: 'closed',        label: 'Avslutade',     stages: ['Disqualified', 'Existing Customer', 'Handed Over'], contactInfo: 'all' },
  // Spans every stage: a lead with nothing to dial or write is the same problem
  // whether it is New or half-worked, and the point of the view is the whole set.
  {
    id: 'no-contact', label: 'Ingen kontakt', stages: [], contactInfo: 'unreachable',
    hint: 'Leads utan både telefonnummer och e-postadress — de som inget annat filter fångar upp',
  },
]

// Swedish country prefix in any written form: "+46", "0046", "+46 (0)",
// "+46 0". Only at the start of a number, so the 46 inside "070 46 12" stays.
const SE_PREFIX = /(?:\+|(?<!\d)00)\s*46[\s-]*(?:\(0\)|0)?/g

/** Phone digits in national form: "+46 70 552 86 36", "0046-70-5528636" and
 *  "070 552 86 36" all read "0705528636". */
export function phoneDigits(s: string): string {
  return s.replace(SE_PREFIX, '0').replace(/\D/g, '')
}

// Lower-cased substring match across identity fields, then digits-only on the
// number fields so the way a number is typed does not matter. Org no ignores
// the dash ("5566778899" finds "556677-8899"); phone also reads +46 and 0046
// as the leading 0, so "070 552" finds "+46 70 552 86 36" and the reverse.
// Fewer than 4 digits is too short to be a number fragment. RADAR-SE-002 /
// RADAR-SE-003.
export function matchesGlobal(l: LeadRow, gl: string): boolean {
  const hay = `${l.company} ${l.contact} ${l.orgNo} ${l.email} ${l.phone}`.toLowerCase()
  if (hay.includes(gl)) return true
  const glDigits = gl.replace(/\D/g, '')
  if (glDigits.length < 4) return false
  if ((l.orgNo || '').replace(/\D/g, '').includes(glDigits)) return true
  if (!l.phone) return false
  return l.phone.replace(/\D/g, '').includes(glDigits) || phoneDigits(l.phone).includes(phoneDigits(gl))
}

/** True when `a` holds exactly the values in `b`.
 *
 * Compares against the distinct values of `b`, not its length: with a plain
 * length check, a Set of {a,b} matched a list of [a,a]. Every caller today
 * passes a hand-written duplicate-free list from QUICK_VIEWS, so nothing was
 * reading wrong — but the name promises set equality, so it should hold. */
export function setsEqual(a: Set<string>, b: string[]): boolean {
  const distinct = new Set(b)
  if (a.size !== distinct.size) return false
  for (const v of distinct) if (!a.has(v)) return false
  return true
}

/** Whether the filter state is exactly what this view selects.
 *
 * Both axes have to agree. Matching on stages alone left "Fresh" lit while the
 * contact-info filter was still on 'unreachable', so the pills claimed a row
 * set that was not on screen. */
export function isQuickViewActive(f: Filters, v: QuickView): boolean {
  // "Alla utom" with nothing ticked filters nothing, the same as include.
  return (f.stage.mode === 'include' || f.stage.values.size === 0)
    && setsEqual(f.stage.values, v.stages)
    && f.contactInfo === v.contactInfo
}

/** How many leads the view would show on its own, ignoring every other filter.
 *
 * Deliberately not "how many are visible right now": the number's job is to say
 * how much work is waiting in a queue before you switch to it. */
export function quickViewCount(leads: LeadRow[], v: QuickView): number {
  return leads.reduce((n, l) => {
    if (v.stages.length && !v.stages.includes(l.stage)) return n
    if (v.contactInfo !== 'all' && isUnreachable(l) !== (v.contactInfo === 'unreachable')) return n
    return n + 1
  }, 0)
}

/** The filter patch a view applies. Only the keys a view owns. */
export function quickViewFilters(v: QuickView): Pick<Filters, 'stage' | 'contactInfo'> {
  return {
    stage: { mode: 'include', values: new Set(v.stages) },
    contactInfo: v.contactInfo,
  }
}

// Which sort keys are numeric — affects default direction when a column
// is clicked for the first time (numeric → desc feels natural; text → asc).
export const SORT_NUMERIC: Record<SortKey, boolean> = {
  score: true,
  underlag: true,
  company: false,
  region: false,
  city: false,
  contact: false,
  phone: false,
  email: false,
  callCount: true,
  gotContact: true,
  rating: true,
  stage: false,
  sfStatus: false,
  booked: true,
  likely: true,
  leadDate: false,
  moveDate: false,
  meetingDate: false,
  seller: false,
  owner: true,
  orgNo: false,
}

// The filter keys that belong to a single column dropdown. `global`,
// `contactInfo` and `doNotContact` are excluded: no dropdown ever skips them.
export type ColumnFilterKey = Exclude<keyof Filters, 'global' | 'contactInfo' | 'doNotContact'>

/** Filter text normalized once per filter change instead of once per lead. */
export interface PreparedFilters {
  /** Only the global term needs pre-normalising; every column operator
   *  normalises its own operand inside matchesText. */
  global: string
}

export function prepareFilters(f: Filters): PreparedFilters {
  return { global: f.global.trim().toLowerCase() }
}

/** One lead's standing against the filter set, evaluated column by column. */
export interface FilterEval {
  /** Global search and the two toolbar three-ways — no dropdown skips these. */
  globalOk: boolean
  contactInfoOk: boolean
  doNotContactOk: boolean
  /** Column filters this lead fails. Empty means it passes all of them. */
  failed: ColumnFilterKey[]
}

// Evaluates every column filter for one lead in a single pass. The dropdowns
// need "which rows would be visible if this one column's filter were lifted",
// which used to mean re-filtering the whole list once per column. A lead
// qualifies for column K's dropdown when it fails nothing, or fails only K —
// so one pass recording the failures answers all of them.
export function evaluateFilters(l: LeadRow, f: Filters, p: PreparedFilters): FilterEval {
  const failed: ColumnFilterKey[] = []
  const fail = (ok: boolean, key: ColumnFilterKey) => { if (!ok) failed.push(key) }

  fail(matchesText(l.company, f.company), 'company')
  fail(matchesText(l.contact, f.contact), 'contact')
  fail(matchesText(l.orgNo, f.orgNo), 'orgNo')

  fail(matchesSet(regionOfLocation(l.location), f.region), 'region')
  fail(matchesSet(cityOfLocation(l.location), f.city), 'city')
  fail(matchesSet(scoreBucketOf(l.score), f.scoreBucket), 'scoreBucket')
  fail(matchesSet(l.phone ? 'Has phone' : 'No phone', f.hasPhone), 'hasPhone')
  fail(matchesSet(EMAIL_STATUS_LABEL[l.emailStatus] || 'Okänd', f.emailStatus), 'emailStatus')

  const calls = l.attempts.filter(Boolean).length
  fail(matchesSet(calls >= 4 ? '4+' : String(calls), f.callCount), 'callCount')
  fail(matchesSet(l.gotContact ? 'Yes' : 'No', f.gotContact), 'gotContact')

  const rating = l.feedback?.rating || 0
  fail(matchesSet(rating === 0 ? 'Unrated' : `${rating}★`, f.rating), 'rating')
  fail(matchesSet(l.stage, f.stage), 'stage')
  fail(matchesSet(sfStatusLabelOf(l), f.sfStatus), 'sfStatus')
  fail(matchesSet(l.bookedMeeting ? 'Yes' : 'No', f.booked), 'booked')
  fail(matchesSet(l.likelyBooking ? 'Yes' : 'No', f.likely), 'likely')
  fail(matchesSet(leadDateBucketOf(l.leadDate), f.leadDate), 'leadDate')
  fail(matchesSet(moveDateBucketOf(l.moveDate), f.moveDate), 'moveDate')
  fail(matchesSet(meetingDateBucketOf(l.meetingAt), f.meetingDate), 'meetingDate')
  fail(matchesSet(l.seller || '(unassigned)', f.seller), 'seller')
  fail(matchesSet(ownerLabelOf(l), f.owner), 'owner')

  // Ranges read the raw value, not the bucket label — that is the point of
  // them. An unrated lead is 0 rather than absent, so "rating at least 1"
  // excludes it instead of matching a missing value. A missing score is
  // null, not 0, and matchesNum already excludes null from any bound.
  fail(matchesNum(l.score, f.scoreRange), 'scoreRange')
  fail(matchesNum(calls, f.callCountRange), 'callCountRange')
  fail(matchesNum(rating, f.ratingRange), 'ratingRange')
  fail(matchesDate(l.leadDate, f.leadDateRange), 'leadDateRange')
  fail(matchesDate(l.moveDate, f.moveDateRange), 'moveDateRange')
  fail(matchesDate(l.meetingAt, f.meetingDateRange), 'meetingDateRange')

  let contactInfoOk = true
  if (f.contactInfo !== 'all') {
    const unreachable = isUnreachable(l)
    contactInfoOk = f.contactInfo === 'reachable' ? !unreachable : unreachable
  }

  return {
    globalOk: !p.global || matchesGlobal(l, p.global),
    contactInfoOk,
    doNotContactOk: matchesDoNotContact(l, f.doNotContact),
    failed,
  }
}

/** Passes the full pipeline: global, every column filter, contactInfo and doNotContact. */
export function passesAll(e: FilterEval): boolean {
  return e.globalOk && e.contactInfoOk && e.doNotContactOk && e.failed.length === 0
}

// Would be visible in column `skip`'s dropdown: passes global and every column
// filter except possibly `skip`'s own. contactInfo deliberately does not apply,
// matching the Excel-style "values given all OTHER filters" behaviour.
export function passesExcept(e: FilterEval, skip: ColumnFilterKey): boolean {
  if (!e.globalOk) return false
  return e.failed.length === 0 || (e.failed.length === 1 && e.failed[0] === skip)
}

// Type-safe comparator. Returns -1/0/1 for `a` vs `b` on `key`. Always
// ascending — direction is applied by the caller. Empty strings sort after
// non-empties so blank rows fall to the bottom in ascending order.
export function compareBy(a: LeadRow, b: LeadRow, key: SortKey): number {
  switch (key) {
    // No score sorts after every real one in ascending order; compareBySorts
    // additionally keeps it last when the chain is descending, since a lead
    // nobody has scored is not "the lowest score" — it's not a score at all.
    case 'score': return cmpNum(sortableScore(a), sortableScore(b))
    case 'underlag': return cmpNum(underlag(a), underlag(b))
    case 'company': return cmpStr(a.company, b.company)
    case 'region': return cmpStr(regionOfLocation(a.location), regionOfLocation(b.location))
    case 'city': return cmpStr(cityOfLocation(a.location), cityOfLocation(b.location))
    case 'contact': return cmpStr(a.contact, b.contact)
    case 'phone': return cmpStr(a.phone, b.phone)
    case 'email': return cmpStr(a.email, b.email)
    case 'callCount': return a.attempts.filter(Boolean).length - b.attempts.filter(Boolean).length
    case 'gotContact': return (a.gotContact ? 1 : 0) - (b.gotContact ? 1 : 0)
    case 'rating': return (a.feedback?.rating || 0) - (b.feedback?.rating || 0)
    case 'stage': return cmpStr(a.stage, b.stage)
    // Leads with no Salesforce lead sort after every real status, so
    // ascending groups them at the end instead of on top of the ones with one.
    case 'sfStatus': return cmpStr(a.sfStatus?.trim() || '\uffff', b.sfStatus?.trim() || '\uffff')
    case 'booked': return (a.bookedMeeting ? 1 : 0) - (b.bookedMeeting ? 1 : 0)
    case 'likely': return (a.likelyBooking ? 1 : 0) - (b.likelyBooking ? 1 : 0)
    case 'leadDate': return cmpCode(a.leadDate, b.leadDate)
    case 'moveDate': return cmpStr(a.moveDate || '', b.moveDate || '')
    case 'meetingDate': return cmpStr(a.meetingAt || '', b.meetingAt || '')
    case 'seller': return cmpStr(a.seller || '', b.seller || '')
    // Unclaimed sorts after every named owner rather than before, so ascending
    // by owner groups the pool at the end instead of burying real names under it.
    case 'owner': return cmpStr(a.assignedTo || '\uffff', b.assignedTo || '\uffff')
    case 'orgNo': return cmpStr(a.orgNo, b.orgNo)
  }
}
function cmpStr(a: string, b: string): number {
  if (!a && b) return 1
  if (a && !b) return -1
  return a.localeCompare(b)
}
// By code point rather than localeCompare: the server sorts Tabell's first page
// (app/api/features/leads/table_order.py) and has to agree with this exactly,
// in every browser locale. An ISO date or a UUID orders the same either way,
// except that Norwegian collation reads "aa" in an id as å.
function cmpCode(a: string, b: string): number {
  if (!a && b) return 1
  if (a && !b) return -1
  return a < b ? -1 : a > b ? 1 : 0
}
function cmpNum(a: number | null, b: number | null): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return a - b
}

/** Longest sort chain kept. Three is already more than anyone reasons about,
 *  and an unbounded chain makes the toolbar summary unreadable. */
export const MAX_SORTS = 3

/**
 * Compare on a chain of columns, falling through to the next on a tie.
 *
 * The final tiebreak on id is what makes the order total: without it two leads
 * equal on every sorted column swap places between renders, and the row the
 * seller was about to click moves.
 */
export function compareBySorts(a: LeadRow, b: LeadRow, sorts: ColumnSort[]): number {
  for (const s of sorts) {
    const d = compareBySort(a, b, s)
    if (d !== 0) return d
  }
  return cmpCode(a.id, b.id)
}

/** One link of the chain, direction applied; 0 on a tie. */
export function compareBySort(a: LeadRow, b: LeadRow, s: ColumnSort): number {
  const d = compareBy(a, b, s.key)
  if (d === 0) return 0
  // A missing score, underlag or lead date is never "lower" or "higher", so it stays
  // last whether the column is sorted ascending or descending — only a real
  // gap between two values flips with direction.
  if (isMissing(a, s.key) || isMissing(b, s.key)) return d
  return s.dir === 'desc' ? -d : d
}

// The columns the server can sort Tabell's first page on (YCRM-83).
const FIRST_PAGE_SORT_KEYS: ReadonlySet<SortKey> = new Set<SortKey>(['score', 'leadDate'])

/**
 * The `sort` parameter that has the server return the first rows of this order,
 * `-score,-leadDate` for score high to low then newest first. Null when a key in
 * the chain is one the server does not sort on.
 */
export function firstPageSortParam(sorts: ColumnSort[]): string | null {
  if (!sorts.length || sorts.some(s => !FIRST_PAGE_SORT_KEYS.has(s.key))) return null
  return sorts.map(s => (s.dir === 'desc' ? '-' : '') + s.key).join(',')
}

function isMissing(l: LeadRow, key: SortKey): boolean {
  if (key === 'score') return sortableScore(l) == null
  if (key === 'underlag') return underlag(l) == null
  if (key === 'leadDate') return !l.leadDate
  return false
}

/**
 * Put `key` at the front of the chain, or toggle it if it is already there.
 *
 * Sorting a new column keeps the previous ones behind it as tie-breakers rather
 * than discarding them, so "stage, then score" is two ordinary clicks and needs
 * no modifier key nobody would discover. Asking for the direction a column
 * already has removes it, which is how a chain gets shortened.
 */
export function applySort(
  sorts: ColumnSort[], key: SortKey, dir: 'asc' | 'desc' | null,
): ColumnSort[] {
  const without = sorts.filter(s => s.key !== key)
  if (dir === null) return without
  return [{ key, dir }, ...without].slice(0, MAX_SORTS)
}

/**
 * The direction a header click moves a column to: its first direction, then
 * the opposite, then off. A numeric column starts highest-first, a text column
 * A to Ö, so clicking "Poäng" twice gives lowest-first rather than no sort.
 */
export function nextHeaderSort(
  current: 'asc' | 'desc' | null, numeric: boolean,
): 'asc' | 'desc' | null {
  const first = numeric ? 'desc' : 'asc'
  if (current === null) return first
  if (current === first) return first === 'desc' ? 'asc' : 'desc'
  return null
}

export function sortDirOf(sorts: ColumnSort[], key: SortKey): 'asc' | 'desc' | null {
  return sorts.find(s => s.key === key)?.dir ?? null
}

/** 1-based position in the chain, or 0 when the column is not sorted. Rendered
 *  next to the arrow so a tie-breaker is distinguishable from the primary. */
export function sortRankOf(sorts: ColumnSort[], key: SortKey): number {
  const i = sorts.findIndex(s => s.key === key)
  return i < 0 ? 0 : i + 1
}

export function emailTitle(email: string, s: EmailStatus): string {
  const label =
    s === 'deliverable' ? 'Levererbar' :
    s === 'risky' ? 'Riskabel — accepteras men garanteras inte' :
    s === 'undeliverable' ? 'Studsat — skicka inte' :
    'Okänd — kunde inte verifieras'
  return `${email}\n${label}`
}


/** Whether one column's filter is doing anything.
 *
 * Not the same as "has a value": an operator that needs a term is inert until
 * it has one, so a half-typed "does not contain" must not count as active or
 * the clear-all button lies about how many filters are on.
 */
export function isColumnActive(f: Filters, key: ColumnFilterKey): boolean {
  const v = f[key] as unknown
  if (v && typeof v === 'object') {
    if ('values' in (v as object)) return isSetActive(v as SetFilter)
    if ('op' in (v as object)) {
      const op = (v as { op: string }).op
      if (op === 'any') return false
      if ('from' in (v as object)) return isDateActive(v as DateFilter)
      if ('a' in (v as object)) return isNumActive(v as NumFilter)
      return isTextActive(v as TextFilter)
    }
  }
  return false
}

export const COLUMN_FILTER_KEYS: ColumnFilterKey[] = (
  Object.keys(EMPTY_FILTERS) as (keyof Filters)[]
).filter(k => k !== 'global' && k !== 'contactInfo' && k !== 'doNotContact') as ColumnFilterKey[]

export function countActiveFilters(f: Filters): number {
  let n = COLUMN_FILTER_KEYS.filter(k => isColumnActive(f, k)).length
  if (f.global.trim()) n += 1
  if (f.contactInfo !== 'all') n += 1
  if (f.doNotContact !== 'all') n += 1
  return n
}


/** Column headings, for the toolbar's sort summary. Kept beside SortKey so a
 *  new sortable column cannot be added without giving it a name. */
export const SORT_LABEL: Record<SortKey, string> = {
  score: 'Poäng',
  underlag: 'Underlag',
  company: 'Företag',
  region: 'Län',
  city: 'Ort',
  contact: 'Kontakt',
  phone: 'Telefon',
  email: 'E-post',
  callCount: 'Samtal',
  gotContact: 'Fick kontakt',
  rating: 'Betyg',
  stage: 'Stadie',
  sfStatus: 'SF-status',
  booked: 'Bokad',
  likely: 'Sannolikt bok',
  leadDate: 'Leaddatum',
  moveDate: 'Flyttdatum',
  meetingDate: 'Möte',
  seller: 'Säljare',
  owner: 'Ägare',
  orgNo: 'Org.nr',
}
